import React, { useEffect, useState } from 'react';
import api from '../services/api';
import Modal from '../components/Modal';
import toast from 'react-hot-toast';
import { Plus, Edit2, Trash2, ArrowLeftRight, TrendingUp, TrendingDown, Wallet, Building2 } from 'lucide-react';
import { ApprovalBadge, RowActions, PendingApprovalBanner, PendingCountBadge } from '../components/ApprovalActions';
import { formatCurrency, formatDate, today } from '../utils/helpers';

const emptyBank = { bank_name:'', account_name:'', account_no:'', ifsc_code:'', branch:'', account_type:'current', opening_balance:0, opening_balance_date:today(), upi_id:'', contact_person:'', contact_phone:'', notes:'', status:1 };
const emptyTransfer = { from_bank_id:'', to_bank_id:'', amount:0, txn_date:today(), notes:'' };
const emptyAdjust = { bank_id:'', type:'credit', amount:0, txn_date:today(), description:'', notes:'' };

export default function Banks() {
  const [list, setList] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(emptyBank);
  const [editId, setEditId] = useState(null);
  const [transferOpen, setTransferOpen] = useState(false);
  const [transferForm, setTransferForm] = useState(emptyTransfer);
  const [adjustOpen, setAdjustOpen] = useState(false);
  const [adjustForm, setAdjustForm] = useState(emptyAdjust);

  const load = () => api.get('/banks').then(r => setList(r.data.data || []));
  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!form.bank_name) return toast.error('Bank name required');
    try {
      if (editId) await api.put('/banks/' + editId, form);
      else await api.post('/banks', form);
      toast.success('Saved'); setOpen(false); setForm(emptyBank); setEditId(null); load();
    } catch (e) { toast.error(e?.response?.data?.message || 'Error'); }
  };
  const edit = (b) => { setForm({...emptyBank, ...b}); setEditId(b.id); setOpen(true); };

  const saveTransfer = async () => {
    if (!transferForm.from_bank_id || !transferForm.to_bank_id) return toast.error('Select both accounts');
    if (transferForm.from_bank_id === transferForm.to_bank_id) return toast.error('Accounts must differ');
    if (!transferForm.amount || transferForm.amount <= 0) return toast.error('Enter amount');
    try { await api.post('/banks/transfer', transferForm); toast.success('Transfer complete'); setTransferOpen(false); setTransferForm(emptyTransfer); load(); }
    catch (e) { toast.error(e?.response?.data?.message || 'Error'); }
  };

  const saveAdjust = async () => {
    if (!adjustForm.bank_id) return toast.error('Select bank');
    if (!adjustForm.amount || adjustForm.amount <= 0) return toast.error('Enter amount');
    try { await api.post('/banks/adjust', adjustForm); toast.success('Adjusted'); setAdjustOpen(false); setAdjustForm(emptyAdjust); load(); }
    catch (e) { toast.error('Error'); }
  };

  const totalBalance = list.reduce((s, b) => s + parseFloat(b.current_balance||0), 0);
  const cashTotal = list.filter(b => b.account_type === 'cash').reduce((s, b) => s + parseFloat(b.current_balance||0), 0);
  const bankTotal = totalBalance - cashTotal;

  const pendingCount = list.filter(x => x.approval_status === 'pending').length;
  return (
    <div>
      <div className="stats-grid">
        <div className="stat-card green">
          <div className="stat-label"><Wallet size={12} style={{verticalAlign:'middle'}}/> Total Balance</div>
          <div className="stat-value">{formatCurrency(totalBalance)}</div>
          <div className="stat-sub">{list.length} account{list.length!==1?'s':''}</div>
        </div>
        <div className="stat-card">
          <div className="stat-label"><Building2 size={12} style={{verticalAlign:'middle'}}/> Bank Accounts</div>
          <div className="stat-value">{formatCurrency(bankTotal)}</div>
        </div>
        <div className="stat-card orange">
          <div className="stat-label"><Wallet size={12} style={{verticalAlign:'middle'}}/> Cash in Hand</div>
          <div className="stat-value">{formatCurrency(cashTotal)}</div>
        </div>
      </div>

      <div className="card">
        <div className="card-header">
          <h3>Bank Accounts ({list.length})<PendingCountBadge count={pendingCount}/></h3>
          <div className="actions-group">
            <button className="btn btn-gray" onClick={() => { setAdjustForm(emptyAdjust); setAdjustOpen(true); }}><TrendingUp size={14}/> Adjust</button>
            <button className="btn btn-gray" onClick={() => { setTransferForm(emptyTransfer); setTransferOpen(true); }}><ArrowLeftRight size={14}/> Transfer</button>
            <button className="btn btn-blue" onClick={() => { setForm(emptyBank); setEditId(null); setOpen(true); }}><Plus size={14}/> Add Bank</button>
          </div>
        </div>
        <div className="table-wrap">
          <table>
            <thead><tr><th>Bank</th><th>Account Name</th><th>Account No</th><th>Type</th><th>IFSC</th><th className="td-num">Opening</th><th>Open Date</th><th className="td-num">Current</th><th>Txns</th><th>Status</th><th>Approval</th><th>Actions</th></tr></thead>
            <tbody>
              {list.length ? list.map(b => (
                <tr key={b.id} style={b.approval_status==='pending'?{background:'#fffbeb'}:{}}>
                  <td><strong>{b.bank_name}</strong></td>
                  <td>{b.account_name || '-'}</td>
                  <td>{b.account_no || '-'}</td>
                  <td><span className="badge badge-blue" style={{textTransform:'uppercase'}}>{b.account_type}</span></td>
                  <td>{b.ifsc_code || '-'}</td>
                  <td className="td-num">{formatCurrency(b.opening_balance)}</td>
                  <td>{formatDate(b.opening_balance_date)}</td>
                  <td className={'td-num ' + (parseFloat(b.current_balance) < 0 ? 'text-red' : 'text-green')}><strong>{formatCurrency(b.current_balance)}</strong></td>
                  <td className="td-center">{b.txn_count || 0}</td>
                  <td><span className={'badge ' + (b.status == 1 ? 'badge-green' : 'badge-red')}>{b.status == 1 ? 'Active' : 'Inactive'}</span></td>
                  <td>
                    <RowActions endpoint="/banks" row={b} onChange={load} onEdit={edit}/>
                  </td>
                </tr>
              )) : <tr><td colSpan="12" className="empty">No bank accounts yet</td></tr>}
            </tbody>
          </table>
        </div>
      </div>

      {/* Add/Edit Bank */}
      <Modal open={open} onClose={()=>setOpen(false)} title={editId?'Edit Bank Account':'Add Bank Account'} size="lg"
        footer={<>
          <button className="btn btn-gray" onClick={()=>setOpen(false)}>Cancel</button>
          <button className="btn btn-blue" onClick={save}>{editId?'Update':'Create'}</button>
        </>}>
        <div className="form-grid">
          <div className="form-group"><label>Bank Name *</label><input value={form.bank_name} onChange={e=>setForm({...form, bank_name:e.target.value})}/></div>
          <div className="form-group"><label>Account Name</label><input value={form.account_name} onChange={e=>setForm({...form, account_name:e.target.value})}/></div>
          <div className="form-group"><label>Account Type</label>
            <select value={form.account_type} onChange={e=>setForm({...form, account_type:e.target.value})}>
              <option value="savings">Savings</option>
              <option value="current">Current</option>
              <option value="cash">Cash Account</option>
              <option value="od">Overdraft</option>
              <option value="cc">Cash Credit</option>
              <option value="loan">Loan</option>
            </select>
          </div>
          <div className="form-group"><label>Account Number</label><input value={form.account_no} onChange={e=>setForm({...form, account_no:e.target.value})}/></div>
          <div className="form-group"><label>IFSC Code</label><input value={form.ifsc_code} onChange={e=>setForm({...form, ifsc_code:e.target.value.toUpperCase()})}/></div>
          <div className="form-group"><label>Branch</label><input value={form.branch} onChange={e=>setForm({...form, branch:e.target.value})}/></div>
          <div className="form-group"><label>UPI ID</label><input value={form.upi_id} onChange={e=>setForm({...form, upi_id:e.target.value})}/></div>
          <div className="form-group"><label>Contact Person</label><input value={form.contact_person} onChange={e=>setForm({...form, contact_person:e.target.value})}/></div>
          <div className="form-group"><label>Contact Phone</label><input value={form.contact_phone} onChange={e=>setForm({...form, contact_phone:e.target.value})}/></div>
          <div className="form-group"><label>Opening Balance</label><input type="number" value={form.opening_balance} onChange={e=>setForm({...form, opening_balance:e.target.value})}/></div>
          <div className="form-group"><label>Opening Balance Date</label><input type="date" value={form.opening_balance_date||''} onChange={e=>setForm({...form, opening_balance_date:e.target.value})}/></div>
          <div className="form-group"><label>Status</label>
            <select value={form.status} onChange={e=>setForm({...form, status:e.target.value})}>
              <option value={1}>Active</option><option value={0}>Inactive</option>
            </select>
          </div>
          <div className="form-group full-width"><label>Notes</label><textarea value={form.notes} onChange={e=>setForm({...form, notes:e.target.value})}/></div>
        </div>
      </Modal>

      {/* Transfer */}
      <Modal open={transferOpen} onClose={()=>setTransferOpen(false)} title="Transfer Between Accounts"
        footer={<>
          <button className="btn btn-gray" onClick={()=>setTransferOpen(false)}>Cancel</button>
          <button className="btn btn-blue" onClick={saveTransfer}>Transfer</button>
        </>}>
        <div className="form-grid">
          <div className="form-group"><label>From Account *</label>
            <select value={transferForm.from_bank_id} onChange={e=>setTransferForm({...transferForm, from_bank_id:e.target.value})}>
              <option value="">-- Select --</option>
              {list.map(b=><option key={b.id} value={b.id}>{b.bank_name} — {formatCurrency(b.current_balance)}</option>)}
            </select>
          </div>
          <div className="form-group"><label>To Account *</label>
            <select value={transferForm.to_bank_id} onChange={e=>setTransferForm({...transferForm, to_bank_id:e.target.value})}>
              <option value="">-- Select --</option>
              {list.filter(b => b.id != transferForm.from_bank_id).map(b=><option key={b.id} value={b.id}>{b.bank_name}</option>)}
            </select>
          </div>
          <div className="form-group"><label>Amount *</label><input type="number" value={transferForm.amount} onChange={e=>setTransferForm({...transferForm, amount:e.target.value})}/></div>
          <div className="form-group"><label>Date</label><input type="date" value={transferForm.txn_date} onChange={e=>setTransferForm({...transferForm, txn_date:e.target.value})}/></div>
          <div className="form-group full-width"><label>Notes</label><textarea value={transferForm.notes} onChange={e=>setTransferForm({...transferForm, notes:e.target.value})}/></div>
        </div>
      </Modal>

      {/* Adjust */}
      <Modal open={adjustOpen} onClose={()=>setAdjustOpen(false)} title="Manual Balance Adjustment"
        footer={<>
          <button className="btn btn-gray" onClick={()=>setAdjustOpen(false)}>Cancel</button>
          <button className="btn btn-blue" onClick={saveAdjust}>Post</button>
        </>}>
        <div className="form-grid">
          <div className="form-group"><label>Bank *</label>
            <select value={adjustForm.bank_id} onChange={e=>setAdjustForm({...adjustForm, bank_id:e.target.value})}>
              <option value="">-- Select --</option>
              {list.map(b=><option key={b.id} value={b.id}>{b.bank_name}</option>)}
            </select>
          </div>
          <div className="form-group"><label>Type *</label>
            <select value={adjustForm.type} onChange={e=>setAdjustForm({...adjustForm, type:e.target.value})}>
              <option value="credit">Credit (Add money)</option>
              <option value="debit">Debit (Remove money)</option>
            </select>
          </div>
          <div className="form-group"><label>Amount *</label><input type="number" value={adjustForm.amount} onChange={e=>setAdjustForm({...adjustForm, amount:e.target.value})}/></div>
          <div className="form-group"><label>Date</label><input type="date" value={adjustForm.txn_date} onChange={e=>setAdjustForm({...adjustForm, txn_date:e.target.value})}/></div>
          <div className="form-group full-width"><label>Description</label><input value={adjustForm.description} onChange={e=>setAdjustForm({...adjustForm, description:e.target.value})}/></div>
          <div className="form-group full-width"><label>Notes</label><textarea value={adjustForm.notes} onChange={e=>setAdjustForm({...adjustForm, notes:e.target.value})}/></div>
        </div>
      </Modal>
    </div>
  );
}
